'use client';

import type { RecentProductItemProps } from './recent-product-item';
import type { Swiper as SwiperCore } from 'swiper';
import type { ConvertedRecentProductWidgetData } from '@weberus/shared-types';
import type { HomeLogPageNameValue } from '@/app/(pages)/sub-home/[category]/constants';
import { useRef, type FC } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { clientLogStandardized, YaTrackerImpression } from '@weberus/client-tracker';
import { waitUntilClickTimeout } from '@weberus/utils';
import { chunk } from 'lodash-es';
import { ShowAllButton } from 'app/_components/common/show-all-button';
import { navigateTo } from '@/app/_shared/utils/action-navigator';
import { SwiperNavButton } from '../../common';
import { RecentProductItem } from './recent-product-item';

export interface RecentProductProps {
  data: ConvertedRecentProductWidgetData;
  isHome?: boolean;
  pageNameLog: HomeLogPageNameValue;
}

/**
 *  최근 본 상품
 *  @see {@link https://www.figma.com/design/zlHuiOHmYv1ghVDbTHUSvW/%5BGUIDE%5D-%EC%9C%84%EC%A0%AF%2F%EC%98%A4%EB%8D%94%EB%A7%81?node-id=7-6015&t=LKjesAT4c1JcxQYL-1}
 *  [노출 정책]
 *  - 해당 위젯은 Home 에서는 MW 에서만 노출되며 sHome 에서는 PC/MW 모두 노출되어야 합니다.
 *  - 위젯 내 Header 영역은 미노출 입니다.
 */
export const RecentProduct: FC<RecentProductProps> = ({ data, isHome = false, pageNameLog }) => {
  const { header, items } = data;
  const swiperRef = useRef<SwiperCore | null>(null);
  const { objectContainer } = data.logMeta;

  const onClickHandler = async (e: React.MouseEvent<HTMLAnchorElement>, item: RecentProductItemProps) => {
    e.preventDefault();
    await waitUntilClickTimeout(clientLogStandardized('click', pageNameLog, { objectContainer, ...item.logMeta }));
    navigateTo(item.action);
  };

  const itemForPc = chunk<ConvertedRecentProductWidgetData['items'][0]>(items, 4);
  const showNavButton = itemForPc.length > 1;

  const MoreButton = !!header?.more && (
    <ShowAllButton
      boldTitle={header.title}
      title={header.more.title}
      href={header.more.action.web}
      className="px-12"
      onClick={() => clientLogStandardized('click', pageNameLog, header.more?.logMeta)}
    />
  );

  const MobileView = (
    <div className="w-full p-16 pt-0 pc:hidden">
      <div className="rounded-12 bg-static-white py-12 shadow-2">
        <Swiper className="w-full !px-12" slidesPerView="auto">
          {items.map((item) => (
            <SwiperSlide key={item.id} className="mr-8 !w-fit last:mr-0">
              <YaTrackerImpression
                onImpressed={() =>
                  clientLogStandardized('impression', pageNameLog, { objectContainer, ...item.logMeta })
                }>
                <RecentProductItem {...item} onClick={(e) => onClickHandler(e, item)} />
              </YaTrackerImpression>
            </SwiperSlide>
          ))}
        </Swiper>
        {MoreButton}
      </div>
    </div>
  );

  // [정책] 홈은 모바일만 노출 / 서브홈은 둘 다 노출!
  if (isHome) {
    return (
      <div className="flex w-full justify-center bg-fill-neutral-weak1 pc:hidden">
        {/* mobile only */}
        <div className="mt-16 w-full bg-fill-neutral-weak1 pc:my-40 pc:w-auto pc:max-w-pc-size">{MobileView}</div>
      </div>
    );
  }

  return (
    <div className="flex w-full justify-center bg-fill-neutral-weak1">
      <div className="mt-16 w-full bg-fill-neutral-weak1 pc:my-40 pc:w-auto pc:max-w-pc-size">
        {MobileView}
        {/* pc only */}
        <div className="hidden w-full pc:block">
          <div className="relative pc:w-pc-size">
            <div className="relative">
              <Swiper
                slidesPerView={1}
                spaceBetween={20}
                loop
                onSwiper={(swiper: SwiperCore) => {
                  swiperRef.current = swiper;
                }}>
                {itemForPc.map((item, idx) => (
                  <SwiperSlide key={`${idx}`} className="!flex gap-20">
                    {item.map((item) => (
                      <YaTrackerImpression
                        key={item.id}
                        onImpressed={() =>
                          clientLogStandardized('impression', pageNameLog, { objectContainer, ...item.logMeta })
                        }>
                        <RecentProductItem {...item} onClick={(e) => onClickHandler(e, item)} />
                      </YaTrackerImpression>
                    ))}
                  </SwiperSlide>
                ))}
              </Swiper>
              {!!showNavButton && <SwiperNavButton swiperRef={swiperRef} />}
            </div>
            {MoreButton}
          </div>
        </div>
      </div>
    </div>
  );
};
